import dayjs from 'dayjs';

import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import ChartHeader from 'components/charts/ChartHeader';
import DataCards from 'components/charts/DataCards';
import LineChartV2 from 'components/charts/chartJS/LineChart';
import { DynamicListChartContainer } from 'components/charts/styled/Chart.styled';
import AutoComplete from 'components/field/AutoComplete';
import Button from 'components/field/Button';
import DateInput from 'components/field/DateInput';
import EnumSelect from 'components/field/EnumSelect';
import Card from 'components/global/Card';
import Popper from 'components/global/Popper';
import Typography from 'components/global/Typography';
import DynamicList from 'components/list/DynamicList';
import { FilterAction, FilterType, ListComponents } from 'components/list/DynamicListFilterUtils';

import useFetchAndParseOnView from 'hooks/useFetchAndParseOnView';

import { AUTOCOMPLETE, ENUMS, FINANCE } from 'services/consts';

import { colorCombinations } from 'utils/colors';
import { DYNAMICLISTS } from 'utils/constants';

import * as S from './UnitEconomicsDashboard.styled';

const UnitEconomicsDashboard = () => (
	<S.Container>
		<div className="row row-cols-1 mt-3">
			<NumberOfOrders />
		</div>
		<div className="row row-cols-1 mt-3">
			<DeliveryCompanyPLAnalysis />
		</div>
		<div className="row row-cols-1 mt-3">
			<DeliveryCompanyProfitability />
		</div>
		<div className="row row-cols-1 mt-3">
			<DeliveryCompanyTimeAnalysis />
		</div>
		<div className="row row-cols-1 mt-3">
			<ClientAnalysisList />
		</div>
		<div className="row row-cols-1 mt-3">
			<AggregatorInvoiceList />
		</div>
	</S.Container>
);

const NumberOfOrders = () => {
	const [filtersMenuButtonRef, setFilterMenuButtonRef] = useState(null);

	const [pickUpDateFilter, setPickUpDateFilter] = useState({
		start: dayjs().subtract(30, 'day').endOf('day').toISOString(),
		end: dayjs().endOf('day').toISOString()
	});
	const [shipmentCreatedDateFilter, setShipmentCreatedDateFilter] = useState(null);
	const [currencyFilterValue, setCurrencyFilterValue] = useState('SAR');
	const [originCountryFilterValue, setOriginCountryFilterValue] = useState('SA');
	const [destinationCountryFilterValue, setDestinationCountryFilterValue] = useState('SA');

	const body = useMemo(
		() => ({
			filter: [
				...(pickUpDateFilter
					? [
							{
								key: ['pickUpDate'],
								action: FilterAction.between,
								from: pickUpDateFilter.start,
								to: pickUpDateFilter.end
							}
						]
					: []),
				...(shipmentCreatedDateFilter
					? [
							{
								key: ['shipmentCreatedDate'],
								action: FilterAction.between,
								from: shipmentCreatedDateFilter.start,
								to: shipmentCreatedDateFilter.end
							}
						]
					: []),
				...(currencyFilterValue
					? [
							{
								key: ['currency'],
								action: FilterAction.is,
								value: [currencyFilterValue]
							}
						]
					: []),
				...(originCountryFilterValue
					? [
							{
								key: ['originCountry'],
								action: FilterAction.is,
								value: [originCountryFilterValue]
							}
						]
					: []),
				...(destinationCountryFilterValue
					? [
							{
								key: ['destinationCountry'],
								action: FilterAction.is,
								value: [destinationCountryFilterValue]
							}
						]
					: [])
			],
			from: undefined,
			to: undefined
		}),
		[
			pickUpDateFilter,
			shipmentCreatedDateFilter,
			currencyFilterValue,
			originCountryFilterValue,
			destinationCountryFilterValue
		]
	);

	const cardsConfig = [
		{
			accessor: 'totalRevenue',
			label: 'totalRevenue',
			customIcon: 'fa fa-money-bill',
			backgroundColor: 'var(--fa fa-money-bill-bgColor)',
			color: 'var(--fa fa-money-bill-color)'
		},
		{
			accessor: 'totalCost',
			label: 'totalCost',
			customIcon: 'fa fa-money-bill',
			backgroundColor: 'var(--fa fa-money-bill-bgColor)',
			color: 'var(--fa fa-money-bill-color)'
		},
		{
			accessor: 'totalProfit',
			label: 'totalProfit',
			customIcon: 'fa fa-money-bill',
			backgroundColor: 'var(--fa fa-money-bill-bgColor)',
			color: 'var(--fa fa-money-bill-color)'
		},
		{
			accessor: 'margin',
			label: 'margin',
			customIcon: 'fa fa-money-bill',
			backgroundColor: 'var(--fa fa-money-bill-bgColor)',
			color: 'var(--fa fa-money-bill-color)'
		},
		{
			accessor: 'totalShipments',
			label: 'totalAggShipments',
			customIcon: 'fa fa-money-bill',
			backgroundColor: 'var(--fa fa-money-bill-bgColor)',
			color: 'var(--fa fa-money-bill-color)'
		},
		{
			accessor: 'profitPerOrder',
			label: 'profitPerOrder',
			customIcon: 'fa fa-money-bill',
			backgroundColor: 'var(--fa fa-money-bill-bgColor)',
			color: 'var(--fa fa-money-bill-color)'
		}
	];

	const { ref, data, loading } = useFetchAndParseOnView({
		url: FINANCE.GET_UNIT_ECONOMIC_TOTALS_BY_COUNTRY,
		body
	});

	return (
		<div ref={ref}>
			<ChartHeader
				title={'totalNumbers'}
				filter={
					<>
						<Button
							id="filters-button"
							lordIcon="https://storage.googleapis.com/tryoto-public/lordicon-compressed/outline/filter-icon.json.gz"
							ref={setFilterMenuButtonRef}
							variant="borderless"
						/>

						{filtersMenuButtonRef && (
							<Popper refElement={filtersMenuButtonRef} placement="bottom-end">
								{() => (
									<S.PopperMenu>
										<Typography variant="p2" fontWeight="500">
											pickupDate
										</Typography>
										<DateInput value={pickUpDateFilter} setValue={setPickUpDateFilter} rangeMode />
										<Typography variant="p2" fontWeight="500">
											shipmentCreatedDate
										</Typography>
										<DateInput
											value={shipmentCreatedDateFilter}
											setValue={setShipmentCreatedDateFilter}
											rangeMode
										/>
										<Typography variant="p2" fontWeight="500">
											currency
										</Typography>
										<EnumSelect
											enumName={ENUMS.CURRENCY}
											value={currencyFilterValue}
											setValue={setCurrencyFilterValue}
											searchable={false}
										/>
										<Typography variant="p2" fontWeight="500">
											originCountry
										</Typography>
										<EnumSelect
											enumName={ENUMS.COUNTRIES}
											value={originCountryFilterValue}
											setValue={setOriginCountryFilterValue}
											searchable={false}
											labelPrefix="country."
										/>
										<Typography variant="p2" fontWeight="500">
											destinationCountry
										</Typography>
										<EnumSelect
											enumName={ENUMS.COUNTRIES}
											value={destinationCountryFilterValue}
											setValue={setDestinationCountryFilterValue}
											searchable={false}
											labelPrefix="country."
										/>
									</S.PopperMenu>
								)}
							</Popper>
						)}
					</>
				}
			/>
			<DataCards data={data} loading={loading} cardsConfig={cardsConfig} />
		</div>
	);
};

const DeliveryCompanyPLAnalysis = () => {
	const [filtersMenuButtonRef, setFilterMenuButtonRef] = useState(null);

	const [pickUpDateFilter, setPickUpDateFilter] = useState({
		start: dayjs().subtract(30, 'day').endOf('day').toISOString(),
		end: dayjs().endOf('day').toISOString()
	});
	const [shipmentCreatedDateFilter, setShipmentCreatedDateFilter] = useState(null);
	const [currencyFilterValue, setCurrencyFilterValue] = useState('SAR');

	const columns = useMemo(
		() => [
			{ Header: 'name', accessor: 'name', filterType: FilterType.string, showOnMobile: true },
			{
				Header: 'totalCost',
				accessor: 'totalCost',
				filterType: FilterType.float,
				renderCell: ListComponents.CURRENCY
			},
			{
				Header: 'totalRevenue',
				accessor: 'totalRevenue',
				filterType: FilterType.float,
				renderCell: ListComponents.CURRENCY
			},
			{
				Header: 'totalProfit',
				accessor: 'totalProfit',
				filterType: FilterType.float,
				renderCell: ListComponents.CURRENCY
			},
			{
				Header: 'margin',
				accessor: 'margin',
				filterType: FilterType.string
			},
			{
				Header: 'profitPerOrder',
				accessor: 'profitPerOrder',
				filterType: FilterType.string
			},
			{
				Header: 'totalShipments',
				accessor: 'totalShipments',
				filterType: FilterType.float
			}
		],
		[]
	);

	const defaultFilters = useMemo(
		() => [
			...(pickUpDateFilter
				? [
						{
							column: {
								Header: 'pickupDate',
								accessor: 'pickUpDate',
								filterType: FilterType.date,
								renderCell: ListComponents.DATETIME,
								filterRemovable: false,
								customFilterOptions: [
									FilterAction.between,
									FilterAction.after,
									FilterAction.before,
									FilterAction.on
								]
							},
							action: FilterAction.between,
							value: pickUpDateFilter
						}
					]
				: []),
			...(shipmentCreatedDateFilter
				? [
						{
							column: {
								Header: 'shipmentCreatedDate',
								accessor: 'shipmentCreatedDate',
								filterType: FilterType.date,
								renderCell: ListComponents.DATETIME,
								customFilterOptions: [
									FilterAction.between,
									FilterAction.after,
									FilterAction.before,
									FilterAction.on
								]
							},
							action: FilterAction.between,
							value: shipmentCreatedDateFilter
						}
					]
				: []),
			...(currencyFilterValue
				? [
						{
							column: {
								Header: 'currency',
								accessor: 'currency',
								filterType: FilterType.enum,
								enumName: ENUMS.CURRENCY,
								filterRemovable: false,
								customFilterOptions: [FilterAction.is]
							},
							action: FilterAction.is,
							value: currencyFilterValue
						}
					]
				: [])
		],
		[pickUpDateFilter, shipmentCreatedDateFilter, currencyFilterValue]
	);

	return (
		<Card>
			<ChartHeader
				title={'deliveryCompanyPLAnalysis'}
				filter={
					<>
						<Button
							id="filters-button"
							lordIcon="https://storage.googleapis.com/tryoto-public/lordicon-compressed/outline/filter-icon.json.gz"
							ref={setFilterMenuButtonRef}
							variant="borderless"
						/>

						{filtersMenuButtonRef && (
							<Popper refElement={filtersMenuButtonRef} placement="bottom-end">
								{() => (
									<S.PopperMenu>
										<Typography variant="p2" fontWeight="500">
											pickupDate
										</Typography>
										<DateInput value={pickUpDateFilter} setValue={setPickUpDateFilter} rangeMode />
										<Typography variant="p2" fontWeight="500">
											shipmentCreatedDate
										</Typography>
										<DateInput
											value={shipmentCreatedDateFilter}
											setValue={setShipmentCreatedDateFilter}
											rangeMode
										/>
										<Typography variant="p2" fontWeight="500">
											currency
										</Typography>
										<EnumSelect
											enumName={ENUMS.CURRENCY}
											value={currencyFilterValue}
											setValue={setCurrencyFilterValue}
											searchable={false}
										/>
									</S.PopperMenu>
								)}
							</Popper>
						)}
					</>
				}
			/>
			<DynamicListChartContainer>
				<DynamicList
					id={DYNAMICLISTS.UNIT_ECONOMICS_DC_PL_ANALYSIS}
					columns={columns}
					defaultFilters={defaultFilters}
					fetchUrl={FINANCE.GET_UNIT_ECONOMIC_DELIVERY_COMPANY_PL_ANALYSIS}
					showHeader={false}
					showHeaderFilters={false}
					exportName="deliveryCompanyPLAnalysis"
					showColumnCalculation={false}
				/>
			</DynamicListChartContainer>
		</Card>
	);
};

const DeliveryCompanyProfitability = () => {
	const [filtersMenuButtonRef, setFilterMenuButtonRef] = useState(null);

	const [pickUpDateFilter, setPickUpDateFilter] = useState({
		start: dayjs().subtract(30, 'day').endOf('day').toISOString(),
		end: dayjs().endOf('day').toISOString()
	});
	const [shipmentCreatedDateFilter, setShipmentCreatedDateFilter] = useState(null);
	const [clientFilterValue, setClientFilterValue] = useState(null);
	const [currencyFilterValue, setCurrencyFilterValue] = useState('SAR');

	const columns = useMemo(
		() => [
			{
				Header: 'dcName',
				accessor: 'dcName',
				filterType: FilterType.enumMulti,
				enumName: ENUMS.DELIVERY_COMPANY_SETTINGS_FOR_AGGREGATORS,
				showOnMobile: true
			},
			{
				Header: 'tier',
				accessor: 'tier',
				filterType: FilterType.enumMulti,
				enumName: ENUMS.DC_CITY_TIER
			},
			{ Header: 'city', accessor: 'city', filterType: FilterType.string },
			{
				Header: 'cost',
				accessor: 'cost',
				filterType: FilterType.float,
				renderCell: ListComponents.CURRENCY
			},
			{
				Header: 'revenue',
				accessor: 'revenue',
				filterType: FilterType.float,
				renderCell: ListComponents.CURRENCY
			},
			{
				Header: 'profit',
				accessor: 'profit',
				filterType: FilterType.float,
				renderCell: ListComponents.CURRENCY
			},
			{
				Header: 'margin',
				accessor: 'margin',
				filterType: FilterType.string
			},
			{
				Header: 'profitPerOrder',
				accessor: 'profitPerOrder',
				filterType: FilterType.string
			},
			{
				Header: 'totalShipments',
				accessor: 'totalShipments',
				filterType: FilterType.float
			}
		],
		[]
	);

	const defaultFilters = useMemo(
		() => [
			...(pickUpDateFilter
				? [
						{
							column: {
								Header: 'pickupDate',
								accessor: 'pickUpDate',
								filterType: FilterType.date,
								renderCell: ListComponents.DATETIME,
								filterRemovable: false,
								customFilterOptions: [
									FilterAction.between,
									FilterAction.after,
									FilterAction.before,
									FilterAction.on
								]
							},
							action: FilterAction.between,
							value: pickUpDateFilter
						}
					]
				: []),
			...(shipmentCreatedDateFilter
				? [
						{
							column: {
								Header: 'shipmentCreatedDate',
								accessor: 'shipmentCreatedDate',
								filterType: FilterType.date,
								renderCell: ListComponents.DATETIME,
								customFilterOptions: [
									FilterAction.between,
									FilterAction.after,
									FilterAction.before,
									FilterAction.on
								]
							},
							action: FilterAction.between,
							value: shipmentCreatedDateFilter
						}
					]
				: []),
			...(clientFilterValue
				? [
						{
							column: {
								Header: 'client',
								accessor: 'client',
								filterType: FilterType.number,
								filterRemovable: false,
								customFilterOptions: [FilterAction.is]
							},
							action: FilterAction.is,
							value: clientFilterValue
						}
					]
				: []),
			...(currencyFilterValue
				? [
						{
							column: {
								Header: 'currency',
								accessor: 'currency',
								filterType: FilterType.enum,
								enumName: ENUMS.CURRENCY,
								filterRemovable: false,
								customFilterOptions: [FilterAction.is]
							},
							action: FilterAction.is,
							value: currencyFilterValue
						}
					]
				: [])
		],
		[pickUpDateFilter, shipmentCreatedDateFilter, currencyFilterValue, clientFilterValue]
	);

	const clientsAutocompleteColumns = useMemo(
		() => [
			{ accessor: 'ID', width: 100 },
			{ accessor: 'Name', width: 400 }
		],
		[]
	);

	return (
		<Card>
			<ChartHeader
				title={'deliveryCompanyProfitability'}
				filter={
					<>
						<Button
							id="filters-button"
							lordIcon="https://storage.googleapis.com/tryoto-public/lordicon-compressed/outline/filter-icon.json.gz"
							ref={setFilterMenuButtonRef}
							variant="borderless"
						/>

						{filtersMenuButtonRef && (
							<Popper refElement={filtersMenuButtonRef} placement="bottom-end">
								{() => (
									<S.PopperMenu>
										<Typography variant="p2" fontWeight="500">
											pickupDate
										</Typography>
										<DateInput value={pickUpDateFilter} setValue={setPickUpDateFilter} rangeMode />
										<Typography variant="p2" fontWeight="500">
											shipmentCreatedDate
										</Typography>
										<DateInput
											value={shipmentCreatedDateFilter}
											setValue={setShipmentCreatedDateFilter}
											rangeMode
										/>
										<Typography variant="p2" fontWeight="500">
											client
										</Typography>
										<AutoComplete
											autoCompleteName={AUTOCOMPLETE.CLIENTS}
											value={clientFilterValue}
											setValue={(value) => {
												setClientFilterValue(value?.map((item) => item.ID) ?? null);
											}}
											columns={clientsAutocompleteColumns}
											selectionLabelAccessor={['Name']}
											multipleSelection
											fixedContainerWidth={false}
										/>
										<Typography variant="p2" fontWeight="500">
											currency
										</Typography>
										<EnumSelect
											enumName={ENUMS.CURRENCY}
											value={currencyFilterValue}
											setValue={setCurrencyFilterValue}
											searchable={false}
										/>
									</S.PopperMenu>
								)}
							</Popper>
						)}
					</>
				}
			/>
			<DynamicListChartContainer>
				<DynamicList
					id={DYNAMICLISTS.UNIT_ECONOMICS_DC_PROFITABILITY}
					columns={columns}
					defaultFilters={defaultFilters}
					fetchUrl={FINANCE.GET_UNIT_ECONOMIC_DC_PROFITABILITY}
					showHeader={false}
					exportName="deliveryCompanyProfitability"
					showColumnCalculation={false}
				/>
			</DynamicListChartContainer>
		</Card>
	);
};

const DeliveryCompanyTimeAnalysis = () => {
	const { t } = useTranslation();
	const [filtersMenuButtonRef, setFilterMenuButtonRef] = useState(null);

	const [clientFilterValue, setClientFilterValue] = useState(null);
	const [deliveryCompanyFilterValue, setDeliveryCompanyFilterValue] = useState(null);
	const [currencyFilterValue, setCurrencyFilterValue] = useState('SAR');

	const parseData = (data) => {
		const labels = data?.data.map((item) => item.month);
		const datasets = [
			{
				label: t('totalCost'),
				data: data?.data.map((item) => item.totalCost),
				backgroundColor: colorCombinations[0].color,
				borderColor: colorCombinations[0].color
			},
			{
				label: t('totalProfit'),
				data: data?.data.map((item) => item.totalProfit),
				backgroundColor: colorCombinations[1].color,
				borderColor: colorCombinations[1].color
			},
			{
				label: t('totalRevenue'),
				data: data?.data.map((item) => item.totalRevenue),
				backgroundColor: colorCombinations[2].color,
				borderColor: colorCombinations[2].color
			}
		];
		return { labels, datasets };
	};

	const body = useMemo(
		() => ({
			filter: [
				...(clientFilterValue
					? [
							{
								key: ['clientID'],
								action: FilterAction.is,
								value: clientFilterValue
							}
						]
					: []),
				...(deliveryCompanyFilterValue
					? [
							{
								key: ['dcName'],
								action: FilterAction.is,
								value: [deliveryCompanyFilterValue]
							}
						]
					: []),
				...(currencyFilterValue
					? [
							{
								key: ['currency'],
								action: FilterAction.is,
								value: [currencyFilterValue]
							}
						]
					: [])
			],
			from: undefined,
			to: undefined
		}),
		[clientFilterValue, deliveryCompanyFilterValue, currencyFilterValue]
	);

	const clientsAutocompleteColumns = useMemo(
		() => [
			{ accessor: 'ID', width: 100 },
			{ accessor: 'Name', width: 400 }
		],
		[]
	);

	const { ref, data, loading } = useFetchAndParseOnView({
		url: FINANCE.GET_UNIT_ECONOMIC_DC_TIME_ANALYSIS,
		body,
		parseData
	});

	return (
		<Card ref={ref}>
			<S.LineChartContainer>
				<ChartHeader
					title={'deliveryCompanyTimeAnalysis'}
					filter={
						<>
							<Button
								id="filters-button"
								lordIcon="https://storage.googleapis.com/tryoto-public/lordicon-compressed/outline/filter-icon.json.gz"
								ref={setFilterMenuButtonRef}
								variant="borderless"
							/>
							{filtersMenuButtonRef && (
								<Popper refElement={filtersMenuButtonRef} placement="bottom-end">
									{() => (
										<S.PopperMenu>
											<Typography variant="p2" fontWeight="500">
												client
											</Typography>
											<AutoComplete
												autoCompleteName={AUTOCOMPLETE.CLIENTS}
												value={clientFilterValue}
												setValue={(value) => {
													setClientFilterValue(value?.map((item) => item.ID) ?? null);
												}}
												columns={clientsAutocompleteColumns}
												selectionLabelAccessor={['ID', 'Name']}
												multipleSelection
												fixedContainerWidth={false}
											/>
											<Typography variant="p2" fontWeight="500">
												deliveryCompany
											</Typography>
											<EnumSelect
												enumName={ENUMS.DELIVERY_COMPANY_SETTINGS_FOR_AGGREGATORS}
												value={deliveryCompanyFilterValue}
												setValue={setDeliveryCompanyFilterValue}
											/>
											<Typography variant="p2" fontWeight="500">
												currency
											</Typography>
											<EnumSelect
												enumName={ENUMS.CURRENCY}
												value={currencyFilterValue}
												setValue={setCurrencyFilterValue}
												searchable={false}
												valueRemovable={false}
											/>
										</S.PopperMenu>
									)}
								</Popper>
							)}
						</>
					}
				/>
				<LineChartV2 data={data} loading={loading} xTitle="month" yTitle={t(currencyFilterValue)} />
			</S.LineChartContainer>
		</Card>
	);
};

const ClientAnalysisList = () => {
	const [filtersMenuButtonRef, setFilterMenuButtonRef] = useState(null);

	const [pickUpDateFilter, setPickUpDateFilter] = useState({
		start: dayjs().subtract(30, 'day').endOf('day').toISOString(),
		end: dayjs().endOf('day').toISOString()
	});
	const [shipmentCreatedDateFilter, setShipmentCreatedDateFilter] = useState(null);

	const columns = useMemo(
		() => [
			{ Header: 'clientName', accessor: 'clientName', filterType: FilterType.string, showOnMobile: true },
			{ Header: 'clientType', accessor: 'clientType', filterType: FilterType.string, showOnMobile: true },
			{
				Header: 'revenue',
				accessor: 'revenue',
				filterType: FilterType.float,
				renderCell: ListComponents.CURRENCY
			},
			{
				Header: 'cost',
				accessor: 'cost',
				filterType: FilterType.float,
				renderCell: ListComponents.CURRENCY
			},
			{
				Header: 'profit',
				accessor: 'profit',
				filterType: FilterType.float,
				renderCell: ListComponents.CURRENCY
			},
			{
				Header: 'margin',
				accessor: 'margin',
				filterType: FilterType.string
			},
			{
				Header: 'profitPerOrder',
				accessor: 'profitPerOrder',
				filterType: FilterType.string
			},
			{
				Header: 'totalShipments',
				accessor: 'totalShipments',
				filterType: FilterType.float
			}
		],
		[]
	);

	const defaultFilters = useMemo(
		() => [
			...(pickUpDateFilter
				? [
						{
							column: {
								Header: 'pickupDate',
								accessor: 'pickUpDate',
								filterType: FilterType.date,
								renderCell: ListComponents.DATETIME,
								filterRemovable: false,
								customFilterOptions: [
									FilterAction.between,
									FilterAction.after,
									FilterAction.before,
									FilterAction.on
								]
							},
							action: FilterAction.between,
							value: pickUpDateFilter
						},
						...(shipmentCreatedDateFilter
							? [
									{
										column: {
											Header: 'shipmentCreatedDate',
											accessor: 'shipmentCreatedDate',
											filterType: FilterType.date,
											renderCell: ListComponents.DATETIME,
											customFilterOptions: [
												FilterAction.between,
												FilterAction.after,
												FilterAction.before,
												FilterAction.on
											]
										},
										action: FilterAction.between,
										value: shipmentCreatedDateFilter
									}
								]
							: [])
					]
				: [])
		],
		[pickUpDateFilter, shipmentCreatedDateFilter]
	);

	return (
		<Card>
			<ChartHeader
				title={'clientAnalysisList'}
				filter={
					<>
						<Button
							id="filters-button"
							lordIcon="https://storage.googleapis.com/tryoto-public/lordicon-compressed/outline/filter-icon.json.gz"
							ref={setFilterMenuButtonRef}
							variant="borderless"
						/>

						{filtersMenuButtonRef && (
							<Popper refElement={filtersMenuButtonRef} placement="bottom-end">
								{() => (
									<S.PopperMenu>
										<Typography variant="p2" fontWeight="500">
											pickupDate
										</Typography>
										<DateInput value={pickUpDateFilter} setValue={setPickUpDateFilter} rangeMode />
										<Typography variant="p2" fontWeight="500">
											shipmentCreatedDate
										</Typography>
										<DateInput
											value={shipmentCreatedDateFilter}
											setValue={setShipmentCreatedDateFilter}
											rangeMode
										/>
									</S.PopperMenu>
								)}
							</Popper>
						)}
					</>
				}
			/>
			<DynamicListChartContainer>
				<DynamicList
					id={DYNAMICLISTS.UNIT_ECONOMICS_CLIENT_ANALYSIS_LIST}
					columns={columns}
					defaultFilters={defaultFilters}
					fetchUrl={FINANCE.GET_UNIT_ECONOMIC_CLIENT_ANALYSIS_LIST}
					showHeader={false}
					exportName="clientAnalysisList"
					showColumnCalculation={false}
				/>
			</DynamicListChartContainer>
		</Card>
	);
};

const AggregatorInvoiceList = () => {
	const columns = useMemo(
		() => [
			{
				Header: 'shipmentNumber',
				accessor: 'shipmentNumber',
				filterType: FilterType.string
			},
			{
				Header: 'otoOrderId',
				accessor: 'otoOrderId',
				filterType: FilterType.number,
				showOnMobile: true
			},
			{
				Header: 'deliveryCompanyID',
				accessor: 'deliveryCompanyID',
				filterType: FilterType.number
			},
			{
				Header: 'deliveryCompanyName',
				accessor: 'deliveryCompanyName',
				filterType: FilterType.string
			},
			{
				Header: 'deliveryCompanyChargedAmount',
				accessor: 'deliveryCompanyChargedAmount',
				filterType: FilterType.float,
				renderCell: ListComponents.CURRENCY
			},
			{
				Header: 'uploadDate',
				accessor: 'uploadDate',
				filterType: FilterType.date,
				renderCell: ListComponents.DATETIME
			},
			{
				Header: 'clientID',
				accessor: 'clientID',
				filterType: FilterType.number
			},
			{
				Header: 'clientName',
				accessor: 'clientName',
				filterType: FilterType.string
			},
			{
				Header: 'orderWeight',
				accessor: 'orderWeight',
				filterType: FilterType.float,
				show: false
			},
			{
				Header: 'lastStatusOfShipment',
				accessor: 'lastStatusOfShipment',
				filterType: FilterType.enumMulti,
				enumName: ENUMS.ALL_STATUSES,
				renderCell: ListComponents.STATUS,
				show: false
			},
			{
				Header: 'lastStatusDate',
				accessor: 'lastStatusDate',
				filterType: FilterType.date,
				renderCell: ListComponents.DATETIME,
				show: false
			},
			{
				Header: 'pickupDate',
				accessor: 'pickUpDate',
				filterType: FilterType.date,
				renderCell: ListComponents.DATETIME
			},
			{
				Header: 'shipmentCreatedDate',
				accessor: 'shipmentCreatedDate',
				filterType: FilterType.date,
				renderCell: ListComponents.DATETIME
			},
			{
				Header: 'shipmentType',
				accessor: 'shipmentType',
				filterType: FilterType.enumMulti,
				enumName: ENUMS.SHIPMENT_TYPE,
				show: false
			},
			{
				Header: 'clientChargedCredit',
				accessor: 'clientChargedCredit',
				filterType: FilterType.float,
				renderCell: ListComponents.CURRENCY
			},
			{
				Header: 'manuallyChargedCredit',
				accessor: 'manuallyChargedCredit',
				filterType: FilterType.float,
				renderCell: ListComponents.CURRENCY,
				show: false
			},
			{
				Header: 'clientRemainingBalance',
				accessor: 'clientRemainingBalance',
				filterType: FilterType.float,
				renderCell: ListComponents.CURRENCY
			},
			{
				Header: 'creditDescription',
				accessor: 'creditDescription',
				filterType: FilterType.string,
				show: false
			},
			{
				Header: 'contractName',
				accessor: 'contractName',
				filterType: FilterType.string,
				show: false
			},
			{
				Header: 'originCountry',
				accessor: 'originCountry',
				filterType: FilterType.enumMulti,
				enumName: ENUMS.COUNTRIES,
				filterPrefix: 'country.',
				renderCell: ListComponents.COUNTRY,
				show: false
			},
			{
				Header: 'destinationCountry',
				accessor: 'destinationCountry',
				filterType: FilterType.enumMulti,
				enumName: ENUMS.COUNTRIES,
				filterPrefix: 'country.',
				renderCell: ListComponents.COUNTRY,
				show: false
			},
			{
				Header: 'originCity',
				accessor: 'originCity',
				filterType: FilterType.string
			},
			{
				Header: 'destinationCity',
				accessor: 'destinationCity',
				filterType: FilterType.string
			},
			{
				Header: 'originTier',
				accessor: 'originTier',
				filterType: FilterType.enumMulti,
				enumName: ENUMS.DC_CITY_TIER
			},
			{
				Header: 'destinationTier',
				accessor: 'destinationTier',
				filterType: FilterType.enumMulti,
				enumName: ENUMS.DC_CITY_TIER
			},
			{
				Header: 'difference',
				accessor: 'difference',
				filterType: FilterType.float,
				renderCell: ListComponents.CURRENCY
			},
			{
				Header: 'discrepancy',
				accessor: 'discrepancy',
				filterType: FilterType.float,
				renderCell: ListComponents.CURRENCY
			}
		],
		[]
	);

	const defaultFilters = useMemo(
		() => [
			{
				column: columns.find((column) => column.accessor === 'pickUpDate'),
				action: FilterAction.between,
				value: {
					start: dayjs().subtract(30, 'day').endOf('day').toISOString(),
					end: dayjs().endOf('day').toISOString()
				}
			}
		],
		[]
	);

	return (
		<Card>
			<ChartHeader title={'aggregatorInvoiceList'} />
			<DynamicListChartContainer>
				<DynamicList
					id={DYNAMICLISTS.UNIT_ECONOMICS_AGGREGATOR_INVOICE_LIST}
					columns={columns}
					defaultFilters={defaultFilters}
					fetchUrl={FINANCE.GET_UNIT_ECONOMIC_AGGREGATOR_INVOICE_LIST}
					exportName="aggregatorInvoiceList"
					showColumnCalculation={false}
				/>
			</DynamicListChartContainer>
		</Card>
	);
};

export default UnitEconomicsDashboard;
